import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, FileText, AlertTriangle, ChevronRight, Shield, CheckCircle2, Info, Loader, Brain, Crown, BarChart3, Target } from 'lucide-react';
import { authHeaders } from '../context/AuthContext';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  emerald: { accent: '#34D399', bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.20)', bar: '#34D399' },
  amber: { accent: '#FBBF24', bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.20)', bar: '#FBBF24' },
  orange: { accent: '#FB923C', bg: 'rgba(251,146,60,0.08)', border: 'rgba(251,146,60,0.20)', bar: '#FB923C' },
  red: { accent: '#F87171', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.20)', bar: '#F87171' },
};

const TestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPhase('loading');
    setConfig(null);
    setAnswers({});
    setResult(null);
    setError(null);
    loadConfig();
  }, [testId]); // eslint-disable-line

  const loadConfig = async () => {
    try {
      const res = await fetch(`${API}/api/tests/${testId}/config`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Test bulunamadı');
      const data = await res.json();
      setConfig(data);
      setPhase(data.status === 'active' ? 'intro' : 'placeholder');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  };

  const handleAnswer = (qIndex, value) => {
    setAnswers(prev => ({ ...prev, [String(qIndex)]: value }));
  };

  const allAnswered = config?.questions?.every(q => answers[String(q.index)] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setPhase('submitting');
    setError(null);
    try {
      const res = await fetch(`${API}/api/tests/${testId}/submit`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, triggered_by: 'dashboard' }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Gönderim sırasında hata oluştu');
      }
      const data = await res.json();
      setResult(data);
      setPhase('result');
    } catch (e) {
      setError(e.message);
      setPhase('questions');
    }
  };

  /* ─── LOADING ─── */
  if (phase === 'loading') {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <Header />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <Loader className="w-8 h-8 animate-spin" style={{ color: '#67E8F9' }} />
        </div>
      </div>
    );
  }

  /* ─── ERROR ─── */
  if (phase === 'error') {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <Header />
        <main className="max-w-lg mx-auto px-6 py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(251,191,36,0.08)', borderRadius: 16 }}>
            <AlertTriangle className="w-8 h-8" style={{ color: '#FBBF24' }} />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">Bir sorun oluştu</h2>
          <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.5)' }}>{error}</p>
          <button onClick={() => navigate('/dashboard')}
            className="px-8 py-3 text-sm font-bold transition-all hover:scale-[1.02]"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', borderRadius: 4 }}
            data-testid="back-to-dashboard">
            Panelime Dön
          </button>
        </main>
      </div>
    );
  }

  /* ─── PLACEHOLDER (inactive test) ─── */
  if (phase === 'placeholder') {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <Header />
        <main className="max-w-lg mx-auto px-6 py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.08)', borderRadius: 16 }}>
            <Info className="w-8 h-8" style={{ color: '#22D3EE' }} />
          </div>
          <h2 className="text-xl font-bold text-white mb-3" data-testid="placeholder-title">{config?.name}</h2>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.5)' }}>{config?.description}</p>
          <button onClick={() => navigate('/dashboard')}
            className="px-8 py-3 text-sm font-bold transition-all hover:scale-[1.02]"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', borderRadius: 4 }}
            data-testid="placeholder-back">
            Panelime Dön
          </button>
        </main>
      </div>
    );
  }

  /* ─── INTRO ─── */
  if (phase === 'intro') {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 mb-8 text-sm transition-colors hover:text-white"
            style={{ color: '#94A3B8' }} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" /> Panelime Dön
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left — Main Info */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 flex items-center justify-center shrink-0" style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)', borderRadius: 12 }}>
                  <Brain className="w-7 h-7" style={{ color: '#67E8F9' }} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight" data-testid="test-name">{config.full_name}</h1>
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{config.name}</p>
                </div>
              </div>

              <p className="text-base leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {config.description}
              </p>

              <button onClick={() => setPhase('questions')}
                className="flex items-center gap-2 px-8 py-3.5 text-sm font-bold transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C', borderRadius: 4, boxShadow: '0 6px 20px rgba(250,204,21,0.25)' }}
                data-testid="start-test-button">
                Teste Başla <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Right — Stats + Info */}
            <div className="lg:col-span-2 space-y-4">
              <div className="p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(250,204,21,0.08)', borderRadius: 8 }}>
                      <FileText className="w-5 h-5" style={{ color: '#FACC15' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{config.question_count}</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Soru sayısı</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.08)', borderRadius: 8 }}>
                      <Clock className="w-5 h-5" style={{ color: '#22D3EE' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#22D3EE' }}>{config.estimated_time}</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Tahmini süre</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.08)', borderRadius: 8 }}>
                      <Target className="w-5 h-5" style={{ color: '#34D399' }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#34D399' }}>Bilimsel Ölçek</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>{config.source}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4" style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.10)', borderRadius: 8 }}>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(251,191,36,0.7)' }}>{config.disclaimer}</p>
              </div>

              {(testId === 'gpcog_informant' || testId === 'short_iqcode') && (
                <div className="p-4" style={{ background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.10)', borderRadius: 8 }} data-testid="informant-notice">
                  <p className="text-xs font-semibold mb-1" style={{ color: '#67E8F9' }}>Yakın Gözlemci Formu</p>
                  <p className="text-xs" style={{ color: 'rgba(34,211,238,0.6)' }}>Bu formu, değerlendirilen kişiyi yakından tanıyan biri doldurmalıdır.</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* ─── QUESTIONS ─── */
  if (phase === 'questions' || phase === 'submitting') {
    const answeredCount = Object.keys(answers).length;
    const progress = config.question_count > 0 ? (answeredCount / config.question_count) * 100 : 0;

    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setPhase('intro')} className="flex items-center gap-2 text-sm transition-colors hover:text-white"
              style={{ color: '#94A3B8' }} data-testid="back-to-intro">
              <ArrowLeft className="w-4 h-4" /> Geri
            </button>
            <span className="text-sm font-medium" style={{ color: '#94A3B8' }}>{config.full_name}</span>
            <span className="text-sm font-bold" style={{ color: '#FACC15' }}>{answeredCount}/{config.question_count}</span>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="h-1.5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
              <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #FACC15, #F59E0B)', borderRadius: 2 }} data-testid="test-progress-bar" />
            </div>
          </div>

          {/* Instruction */}
          <div className="p-4 mb-6" style={{ background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.10)', borderRadius: 8 }}>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{config.instruction}</p>
          </div>

          {/* Questions */}
          <div className="space-y-4" data-testid="questions-container">
            {config.questions.map((q, i) => {
              const opts = q.response_options?.length ? q.response_options : config.response_options;
              const isAnswered = answers[String(q.index)] !== undefined;
              const colClass = opts.length <= 2 ? 'grid-cols-2' : opts.length >= 6 ? 'grid-cols-2 sm:grid-cols-3' : opts.length === 5 ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4';
              return (
                <div key={q.index} className="p-5 transition-all" style={{
                  background: isAnswered ? 'rgba(250,204,21,0.03)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isAnswered ? 'rgba(250,204,21,0.12)' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: 8,
                }} data-testid={`question-${q.index}`}>
                  <p className="text-sm font-medium text-white mb-3 leading-relaxed">
                    <span className="font-bold mr-2" style={{ color: '#FACC15' }}>{i + 1}.</span>
                    {q.text}
                  </p>
                  {q.domain_label && (
                    <p className="text-[10px] mb-2 uppercase tracking-wider" style={{ color: '#64748B' }}>{q.domain_label}</p>
                  )}
                  <div className={`grid ${colClass} gap-2`}>
                    {opts.map(opt => {
                      const selected = answers[String(q.index)] === opt.value;
                      return (
                        <button key={opt.value}
                          onClick={() => handleAnswer(q.index, opt.value)}
                          className="px-3 py-2.5 text-xs font-medium transition-all"
                          style={{
                            background: selected ? 'rgba(250,204,21,0.10)' : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${selected ? 'rgba(250,204,21,0.30)' : 'rgba(255,255,255,0.06)'}`,
                            color: selected ? '#FCD34D' : '#94A3B8',
                            borderRadius: 4,
                          }}
                          data-testid={`q${q.index}-opt-${opt.value}`}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {error && <p className="text-sm mt-4 text-center" style={{ color: '#FB923C' }}>{error}</p>}

          {/* Submit */}
          <div className="mt-8 flex flex-col items-center">
            <button onClick={handleSubmit}
              disabled={!allAnswered || phase === 'submitting'}
              className="w-full max-w-md py-3.5 text-sm font-bold transition-all hover:scale-[1.02] disabled:opacity-30 disabled:hover:scale-100"
              style={{
                background: allAnswered ? 'linear-gradient(135deg, #FACC15, #F59E0B)' : 'rgba(255,255,255,0.05)',
                color: allAnswered ? '#1A2B3C' : '#64748B',
                borderRadius: 4,
                boxShadow: allAnswered ? '0 6px 20px rgba(250,204,21,0.25)' : 'none',
              }}
              data-testid="submit-test-button">
              {phase === 'submitting' ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" /> Değerlendiriliyor...
                </span>
              ) : allAnswered ? 'Testi Tamamla' : `${config.question_count - answeredCount} soru kaldı`}
            </button>
          </div>
        </main>
      </div>
    );
  }

  /* ─── RESULT ─── */
  if (phase === 'result' && result) {
    const ls = LEVEL_STYLES[result.level_color] || LEVEL_STYLES.amber;

    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
          {/* Safety Warning */}
          {result.safety_flag && (
            <div className="p-6 mb-6 flex items-start gap-4" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: 8 }} data-testid="safety-warning">
              <Shield className="w-6 h-6 mt-0.5 shrink-0" style={{ color: '#FBBF24' }} />
              <div>
                <h3 className="font-bold mb-2" style={{ color: '#FBBF24' }}>Seni Duyuyoruz</h3>
                <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>{result.safety_message}</p>
                <p className="text-sm font-medium" style={{ color: '#FBBF24' }}>{result.safety_resources}</p>
              </div>
            </div>
          )}

          {/* Score — 2 col */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-8">
            {/* Left — Score visual */}
            <div className="lg:col-span-2 flex flex-col items-center justify-center">
              <div className="relative w-36 h-36 mx-auto mb-4">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={ls.accent} strokeWidth="6"
                    strokeLinecap="round" strokeDasharray={`${Math.min(result.percentage, 100) * 2.64} 264`}
                    style={{ filter: `drop-shadow(0 0 8px ${ls.accent}40)` }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-white" data-testid="result-score">{result.raw_score}</span>
                  <span className="text-xs" style={{ color: '#94A3B8' }}>/ {result.max_score}</span>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-2" style={{ background: ls.bg, border: `1px solid ${ls.border}`, borderRadius: 4 }}>
                <CheckCircle2 className="w-4 h-4" style={{ color: ls.accent }} />
                <span className="text-sm font-bold" style={{ color: ls.accent }} data-testid="result-level">{result.level_label}</span>
              </div>
              <h2 className="text-xl font-bold text-white">Sonucunuz Hazır</h2>
            </div>

            {/* Right — Description + CTA */}
            <div className="lg:col-span-3 space-y-4">
              <div className="p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }} data-testid="result-card">
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }} data-testid="result-description">
                  {result.level_description}
                </p>
              </div>

              {/* What it measures */}
              <div className="p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4" style={{ color: '#67E8F9' }} />
                  <h3 className="text-sm font-bold text-white">Bu test ne ölçer?</h3>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>{result.what_it_measures}</p>
              </div>

              {/* Premium upsell */}
              <div className="p-5" style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.06), rgba(217,162,88,0.04))', border: '1px solid rgba(250,204,21,0.15)', borderRadius: 8 }}>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4" style={{ color: '#FACC15' }} />
                  <h3 className="text-sm font-bold" style={{ color: '#FACC15' }}>Detaylı AI Analizi</h3>
                </div>
                <p className="text-xs leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Premium ile AI doktorunuz bu sonuçları tüm sağlık verilerinizle birlikte analiz eder, kişisel öneriler sunar ve zaman içindeki değişimi izler.
                </p>
                <button onClick={() => navigate('/premium')}
                  className="px-5 py-2 text-xs font-bold transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C', borderRadius: 4 }}
                  data-testid="result-premium-btn">
                  Premium ile Detaylı Analiz Al
                </button>
              </div>
            </div>
          </div>

          {/* Domain Scores */}
          {result.extra_data?.domain_scores && (
            <div className="p-5 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }} data-testid="domain-scores">
              <h3 className="text-sm font-bold text-white mb-4">Alan Bazlı Görünüm</h3>
              <div className="space-y-3">
                {Object.entries(result.extra_data.domain_scores).map(([domain, val]) => {
                  const labels = result.extra_data.domain_labels || {};
                  // val can be a number (most tests) or an object (DSM-5: {items, highest, domain_label})
                  const isObj = typeof val === 'object' && val !== null;
                  const pct = isObj ? Math.round((val.highest || 0) / 4 * 100) : val;
                  const label = isObj ? (val.domain_label || labels[domain] || domain) : (labels[domain] || domain);
                  const displayVal = isObj ? `${val.highest || 0}/4` : `${pct}/100`;
                  return (
                    <div key={domain}>
                      <div className="flex justify-between text-xs mb-1">
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                        <span style={{ color: 'rgba(255,255,255,0.35)' }}>{displayVal}</span>
                      </div>
                      <div className="h-1.5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                        <div className="h-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, background: ls.accent, borderRadius: 2 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="p-4 mb-6" style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.10)', borderRadius: 8 }}>
            <p className="text-xs text-center leading-relaxed" style={{ color: 'rgba(251,191,36,0.6)' }}>{result.disclaimer}</p>
          </div>

          {/* Actions */}
          <div className="space-y-3 max-w-md mx-auto">
            <button onClick={async () => {
              try { await fetch(`${API}/api/user/state/refresh`, { method: 'POST', headers: authHeaders() }); } catch(e) {}
              try { fetch(`${API}/api/user/doctor-detail/generate`, { method: 'POST', headers: authHeaders() }); } catch(e) {}
              navigate('/ai-doctor?testCompleted=true&testName=' + encodeURIComponent(config.full_name));
            }}
              className="w-full py-3.5 text-sm font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', color: '#1A2B3C', borderRadius: 4, boxShadow: '0 6px 20px rgba(250,204,21,0.25)' }}
              data-testid="result-back-dashboard">
              <CheckCircle2 className="w-4 h-4" /> Sonuçları Doktoruma Göster
            </button>

            {result.next_recommendation && result.next_recommendation.status !== 'placeholder' && (
              <button onClick={() => navigate(`/test/${result.next_recommendation.test_id}`)}
                className="w-full py-3 text-sm font-medium transition-all flex items-center justify-center gap-2"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', borderRadius: 4 }}
                data-testid="next-test-button-bottom">
                Sıradaki: {result.next_recommendation.name} <ChevronRight className="w-4 h-4" />
              </button>
            )}

            <button onClick={() => navigate('/dashboard')}
              className="w-full py-2.5 text-xs font-medium transition-all"
              style={{ color: '#64748B' }}
              data-testid="back-dashboard-link">
              Panelime Dön
            </button>
          </div>
        </main>
      </div>
    );
  }

  return null;
};

export default TestPage;
